/**
 * Mitarbeiter und Einrichtungscodes.
 *
 * Das ist der erste Bildschirm, den es geben muss: Ohne einen
 * Mitarbeiterdatensatz und einen Einrichtungscode kommt kein Fahrer in die
 * App. Bisher lief das über von Hand abgesetzte SQL-Zeilen — was für den
 * Aufbau in Ordnung war und für den Betrieb nicht.
 *
 * Zwei Dinge sind hier wichtig zu verstehen:
 *
 * 1. `mitarbeiter` und Zugang sind getrennt. Der Datensatz beschreibt eine
 *    Person im Betrieb. Ob und wie sie sich anmeldet, steht woanders
 *    (`auth_user_id`). Ein Fahrer, der noch kein Gerät eingerichtet hat, ist
 *    ein vollständiger Mitarbeiter — er hat nur noch kein Konto.
 *
 * 2. Rolle und Status ändert nur ein aktiver Admin, und nur über die
 *    Datenbankfunktion `mitarbeiter_rolle_status_setzen` (HJ-015, E-036).
 *    Ein direktes Update auf diese Spalten lehnt die Datenbank ab — auch für
 *    einen Admin —, damit jeder Wechsel in `kontoereignis` steht.
 *
 * 3. Der Einrichtungscode wird NUR EINMAL angezeigt. In der Datenbank liegt
 *    nur sein Hash. Das ist Absicht: Wer die Tabelle liest, soll sich damit
 *    kein Gerät einrichten können. Für die Oberfläche heißt das, sie muss den
 *    Code festhalten, bis das Büro ihn weitergegeben hat — ein Neuladen der
 *    Seite verliert ihn unwiederbringlich, und das muss dort stehen.
 */

import { supabase } from '../supabase';
import {
  aendern,
  anlegen,
  aufrufen,
  keineZeileGetroffen,
  Speicherfehler,
} from './speichern';
import { alsKlarfehler } from './fehler';

export type Rolle = 'fahrer' | 'buero' | 'admin';
/**
 * Nur die Beschäftigung (HJ-011, E-036). Ob jemand eingerichtet ist, steht in
 * Konto und Code — „Angelegt" und „Eingeladen" wiederholten das und
 * veralteten („Eingeladen" neben „Code abgelaufen"). Der Wert `ausgetreten`
 * bleibt in der Datenbank, angezeigt wird „Ausgeschieden".
 */
export type Status = 'aktiv' | 'gesperrt' | 'ausgetreten';

export type Mitarbeiter = {
  id: string;
  auth_user_id: string | null;
  personalnummer: string | null;
  vorname: string;
  nachname: string;
  rolle: Rolle;
  status: Status;
  telefon: string | null;
  email: string | null;
  eintritt: string | null;
  austritt: string | null;
  notiz: string | null;
  /**
   * Konto zum Ausprobieren.
   *
   * Ein Testkonto erzeugt echte Daten — Fahrten, Arbeitstage, Spesentage —, die
   * in denselben Tabellen landen wie die echten. Ohne diese Kennzeichnung fällt
   * bei der ersten Monatsauswertung Verpflegungsmehraufwand für jemanden an,
   * der nie unterwegs war.
   */
  ist_testkonto: boolean;
  erstellt_am: string;
  geaendert_am: string;
};

/** Ein Einrichtungscode — der Code selbst steht nirgends mehr. */
export type OffenerCode = {
  id: string;
  mitarbeiter_id: string;
  gueltig_bis: string;
  erstellt_am: string;
  fehlversuche: number;
  eingeloest_am: string | null;
};

export type Mitarbeiterzeile = Mitarbeiter & {
  offenerCode: OffenerCode | null;
  /** Abgelaufen, aber noch nicht eingelöst — braucht einen neuen. */
  codeAbgelaufen: boolean;
  /**
   * Wann zuletzt ein Code eingelöst wurde. Bei Bürorollen der einzige
   * Hinweis, dass die App eingerichtet ist: Ihr Konto ist der Bürozugang,
   * ein eigenes Gerätekonto entsteht nicht.
   */
  appEingerichtetAm: string | null;
};

export const ROLLENTEXT: Record<Rolle, string> = {
  fahrer: 'Fahrer',
  buero: 'Büro',
  admin: 'Admin',
};

export const STATUSTEXT: Record<Status, string> = {
  aktiv: 'Aktiv',
  gesperrt: 'Gesperrt',
  ausgetreten: 'Ausgeschieden',
};

/**
 * Der Seitentext je Rolle (HJ-010) — wortgleich mit HJ-010 in
 * Betrieb_und_Aufgaben.docx. Testkonten stehen bewusst nicht darin; die sind
 * an ihrer Zeile markiert.
 */
export const ROLLENTEXTE: [string, string][] = [
  [
    'Admin',
    'arbeitet im Backoffice wie das Büro und vergibt zusätzlich Zugänge, setzt Passwörter neu und ändert Rollen und Status.',
  ],
  [
    'Büro',
    'arbeitet im Backoffice (Disposition, Stammdaten, Abrechnung) und bekommt bei Bedarf die App für unterwegs.',
  ],
  [
    'Fahrer',
    'nutzt nur die App und richtet sie mit einem Code ein, den das Büro erzeugt.',
  ],
];

/**
 * Was das Büro anlegen darf. Admin wird man danach über „Rolle" — durch einen
 * Admin, mit Protokoll. Die Datenbank lehnt alles andere ab (HJ-015).
 */
export const ANLEGBARE_ROLLEN = ['fahrer', 'buero'] as const;

/**
 * Liste laden.
 *
 * Bewusst zwei Abfragen statt einer verschachtelten: Die Verknüpfung über
 * PostgREST würde beim kleinsten Umbau an den Fremdschlüsseln stillschweigend
 * leere Listen liefern — der klassische „ist bestimmt RLS"-Irrtum. Bei zehn
 * Mitarbeitern kostet die zweite Abfrage nichts.
 */
export async function mitarbeiterLaden(): Promise<Mitarbeiterzeile[]> {
  const [leute, codes] = await Promise.all([
    supabase
      .from('mitarbeiter')
      .select('*')
      .order('nachname', { ascending: true }),
    // Auch die eingelösten: Bei Bürorollen sagt nur der Code, ob die App
    // eingerichtet ist. Je Person ein paar Zeilen — bei zehn Leuten nichts.
    supabase
      .from('einrichtungscode')
      .select('id, mitarbeiter_id, gueltig_bis, erstellt_am, fehlversuche, eingeloest_am'),
  ]);

  if (leute.error) throw new Speicherfehler(alsKlarfehler(leute.error));
  // Codes darf nur das Büro lesen. Scheitert das, ist die Liste trotzdem
  // brauchbar — deshalb hier kein Abbruch, sondern ein leeres Ergebnis.
  const alle = (codes.data ?? []) as OffenerCode[];
  const jetzt = Date.now();

  return (leute.data as Mitarbeiter[]).map((person) => {
    const eigene = alle.filter((c) => c.mitarbeiter_id === person.id);
    const code = eigene.find((c) => c.eingeloest_am === null) ?? null;
    const eingeloest = eigene
      .map((c) => c.eingeloest_am)
      .filter((am): am is string => am !== null)
      .sort();
    return {
      ...person,
      offenerCode: code,
      codeAbgelaufen: code ? new Date(code.gueltig_bis).getTime() < jetzt : false,
      appEingerichtetAm: eingeloest.at(-1) ?? null,
    };
  });
}

export type NeuerMitarbeiter = {
  vorname: string;
  nachname: string;
  rolle: (typeof ANLEGBARE_ROLLEN)[number];
  personalnummer?: string;
  telefon?: string;
  email?: string;
  eintritt?: string;
  istTestkonto?: boolean;
};

export async function mitarbeiterAnlegen(
  neu: NeuerMitarbeiter,
): Promise<Mitarbeiter> {
  return anlegen<Mitarbeiter>('mitarbeiter', {
    vorname: neu.vorname.trim(),
    nachname: neu.nachname.trim(),
    rolle: neu.rolle,
    personalnummer: leerZuNull(neu.personalnummer),
    telefon: leerZuNull(neu.telefon),
    email: leerZuNull(neu.email),
    eintritt: leerZuNull(neu.eintritt),
    ist_testkonto: neu.istTestkonto ?? false,
  });
}

export async function mitarbeiterAendern(
  person: Mitarbeiter,
  werte: Partial<Mitarbeiter>,
): Promise<Mitarbeiter> {
  return aendern<Mitarbeiter>({
    tabelle: 'mitarbeiter',
    id: person.id,
    gelesenerStand: person.geaendert_am,
    werte,
    was: `Der Mitarbeiter ${person.vorname} ${person.nachname}`,
  });
}

/**
 * Rolle und Status setzen — der einzige Weg dafür (HJ-015).
 *
 * Die Funktion prüft den gelesenen Stand wie `aendern()` (E-033): Passt er
 * nicht mehr, kommt KEINE Zeile zurück, und das ist ein Konflikt, kein
 * Erfolg. Was genau, klärt `keineZeileGetroffen()` — dieselbe Nachfrage wie
 * überall sonst. Rolle und Status gehen immer zusammen, damit die Datenbank
 * den letzten aktiven Admin an beiden Hälften erkennt.
 */
export async function rolleStatusSetzen(
  person: Mitarbeiter,
  rolle: Rolle,
  status: Status,
): Promise<Mitarbeiter> {
  const was = `Der Mitarbeiter ${person.vorname} ${person.nachname}`;
  const zeilen = await aufrufen<Mitarbeiter[]>('mitarbeiter_rolle_status_setzen', {
    p_mitarbeiter: person.id,
    p_rolle: rolle,
    p_status: status,
    p_gelesener_stand: person.geaendert_am,
  });
  if (!zeilen || zeilen.length === 0) {
    return keineZeileGetroffen({
      tabelle: 'mitarbeiter',
      id: person.id,
      gelesenerStand: person.geaendert_am,
      was,
      danach:
        'Der neue Stand ist geladen — prüf ihn und wähle noch einmal, falls deine Änderung gelten soll.',
    });
  }
  return zeilen[0];
}

export type ErzeugterCode = { code: string; gueltig_bis: string };

/**
 * Einen Einrichtungscode erzeugen.
 *
 * Die Datenbankfunktion löscht dabei einen eventuell noch offenen Code. Das
 * ist richtig so — zwei gültige Codes für dieselbe Person wären eine offene
 * Tür, von der niemand mehr weiß, wer sie hat.
 */
export async function einrichtungscodeErzeugen(
  mitarbeiterId: string,
): Promise<ErzeugterCode> {
  const ergebnis = await aufrufen<ErzeugterCode[] | ErzeugterCode>(
    'einrichtungscode_erzeugen',
    { p_mitarbeiter: mitarbeiterId },
  );
  const eintrag = Array.isArray(ergebnis) ? ergebnis[0] : ergebnis;
  if (!eintrag?.code) {
    throw new Speicherfehler({
      art: 'unbekannt',
      titel: 'Kein Code zurückgekommen',
      text: 'Die Datenbank hat keinen Code geliefert. Bitte noch einmal versuchen.',
      original: JSON.stringify(ergebnis),
    });
  }
  return eintrag;
}

/** Einen noch nicht eingelösten Code zurückziehen — etwa nach einem Irrtum. */
export async function codeZurueckziehen(mitarbeiterId: string): Promise<void> {
  const { error } = await supabase
    .from('einrichtungscode')
    .delete()
    .eq('mitarbeiter_id', mitarbeiterId)
    .is('eingeloest_am', null);
  if (error) throw new Speicherfehler(alsKlarfehler(error));
}

function leerZuNull(wert?: string): string | null {
  const sauber = wert?.trim();
  return sauber ? sauber : null;
}
